/**
 * Employee Link Modal
 * Shown to users who haven't linked their account to an employee profile
 * Allows them to enter an invitation code to link their account
 */
import { useState } from 'react';
import { XMarkIcon, TicketIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { invitations } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function EmployeeLinkModal({ isOpen, onClose, onSuccess }) {
    const { refreshUser } = useAuth();
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(null);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim()) {
            setError('Por favor, introduce tu código de invitación');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess(null);

        try {
            const result = await invitations.redeem(code.trim().toUpperCase());
            setSuccess(result);

            // Refresh user data to update role and employee link
            await refreshUser();

            // Wait a bit then notify success
            // IMPORTANT: Don't call onClose here - it causes logout!
            // The modal will close automatically because isUnlinked will become false
            // after refreshUser updates the user state
            setTimeout(() => {
                if (onSuccess) onSuccess(result);
                // Don't call onClose - the component will unmount automatically
                // when isUnlinked becomes false in UserLayout
            }, 2000);
        } catch (err) {
            setError(err.message || 'Código inválido o expirado');
        } finally {
            setLoading(false);
        }
    };

    const handleCodeChange = (e) => {
        // Auto-format: uppercase and limit length
        const value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
        if (value.length <= 9) { // Format: XXXX-XXXX
            setCode(value);
            setError('');
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/70">
            <div className="relative bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-cyan-500/20 w-full max-w-md transform transition-all">
                {/* Header */}
                <div className="relative p-6 pb-0">
                    <div className="absolute top-4 right-4">
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex flex-col items-center text-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/20">
                            <UserCircleIcon className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-white">Vincular Cuenta</h2>
                        <p className="text-gray-400 mt-2 text-sm">
                            Introduce el código de invitación que te ha proporcionado tu administrador
                        </p>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6">
                    {success ? (
                        <div className="text-center py-4">
                            <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-white mb-2">
                                ¡Cuenta vinculada!
                            </h3>
                            <p className="text-gray-300">
                                Ahora eres <span className="text-cyan-400 font-medium">{success.employeeName}</span>
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                                Tipo: {success.employeeType}
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Code Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Código de Invitación
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <TicketIcon className="h-5 w-5 text-gray-500" />
                                    </div>
                                    <input
                                        type="text"
                                        value={code}
                                        onChange={handleCodeChange}
                                        placeholder="XXXX-XXXX"
                                        className="w-full pl-12 pr-4 py-4 bg-black/40 border border-white/10 rounded-xl text-white text-center text-2xl tracking-[0.3em] font-mono placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                                        autoFocus
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                                    <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading || !code.trim()}
                                className="w-full py-4 px-6 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Verificando...
                                    </>
                                ) : (
                                    'Vincular mi cuenta'
                                )}
                            </button>
                        </form>
                    )}
                </div>

                {/* Footer hint */}
                {!success && (
                    <div className="px-6 pb-6">
                        <p className="text-center text-gray-500 text-xs">
                            ¿No tienes código? Contacta con tu administrador para obtener uno.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
